import React from "react";
import { Link } from "react-router-dom";
import { HiArrowUpRight } from "react-icons/hi2";
import {
  IoGitBranchOutline,
  IoShieldCheckmarkOutline,
  IoPulseOutline,
  IoCubeOutline,
  IoHardwareChipOutline,
  IoServerOutline,
} from "react-icons/io5";
import { SectionHeading, Reveal } from "./ui";

const domains = [
  { id: "rtl-architecture", n: "01", title: "RTL & Architecture", description: "Micro-architecture, RTL development, IP integration, CDC/lint and low-power design.", icon: IoGitBranchOutline, span: "lg:col-span-7", featured: true },
  { id: "design-verification", n: "02", title: "Design Verification", description: "UVM, formal/ABV, coverage closure, regression and verification infrastructure.", icon: IoShieldCheckmarkOutline, span: "lg:col-span-5" },
  { id: "dft", n: "03", title: "DFT", description: "Scan/ATPG, MBIST/LBIST, test architecture, signoff and silicon debug.", icon: IoPulseOutline, span: "lg:col-span-4" },
  { id: "physical-design-sta", n: "04", title: "Physical Design & STA", description: "Synthesis, floorplan through routing, timing closure, physical verification and signoff.", icon: IoCubeOutline, span: "lg:col-span-4" },
  { id: "fpga-emulation", n: "05", title: "FPGA & Emulation", description: "FPGA design, prototyping, emulation and pre-silicon validation.", icon: IoHardwareChipOutline, span: "lg:col-span-4" },
  { id: "silicon-embedded", n: "06", title: "Silicon & Embedded", description: "Bring-up, characterization, product/test engineering, firmware, drivers and BSP.", icon: IoServerOutline, span: "lg:col-span-12", wide: true },
];

export default function CapabilityDomains() {
  return (
    <section className="section relative overflow-hidden bg-paper">
      <div className="bg-grid-light absolute inset-0 [mask-image:linear-gradient(to_bottom,black,transparent_70%)]" aria-hidden="true" />
      <div className="container-x relative">
        <SectionHeading index="03" eyebrow="Capabilities" title="Six domains. One accountable team.">
          Senior-led engineering across the silicon lifecycle—from architecture to embedded software.
        </SectionHeading>

        <div className="grid gap-4 lg:grid-cols-12 lg:gap-5">
          {domains.map((d, i) => (
            <Reveal key={d.id} delay={(i % 3) * 80} className={d.span}>
              <Link
                to={`/services#${d.id}`}
                className={`group relative flex h-full min-h-[15rem] flex-col justify-between overflow-hidden rounded-3xl border p-8 transition duration-300 md:p-10 ${
                  d.featured
                    ? "on-dark border-navy-700 bg-navy-900 text-white hover:border-brand-light/50"
                    : "border-slate-200 bg-white hover:-translate-y-1 hover:border-brand/40 hover:shadow-[0_24px_60px_-24px_rgba(36,87,245,0.35)]"
                } ${d.wide ? "md:flex-row md:items-end md:gap-12" : ""}`}
              >
                {d.featured && (
                  <>
                    <div className="glow-blue absolute -right-20 -top-20 h-80 w-80 opacity-60 transition-opacity group-hover:opacity-90" aria-hidden="true" />
                    <div className="bg-grid-dark absolute inset-0 opacity-60 [mask-image:linear-gradient(135deg,black,transparent_60%)]" aria-hidden="true" />
                  </>
                )}
                <div className="relative flex items-start justify-between">
                  <d.icon className={`h-8 w-8 ${d.featured ? "text-brand-light" : "text-brand"}`} aria-hidden="true" />
                  <span className={`font-mono text-xs ${d.featured ? "text-white/40" : "text-slate-300"}`}>{d.n}</span>
                </div>
                <div className={`relative mt-14 ${d.wide ? "md:mt-0 md:flex-1" : ""}`}>
                  <h3 className={`font-display text-2xl font-semibold tracking-tight md:text-3xl ${d.featured ? "text-white" : "text-ink"}`}>{d.title}</h3>
                  <p className={`mt-3 max-w-lg leading-relaxed ${d.featured ? "text-slate-300" : "text-slate-600"}`}>{d.description}</p>
                </div>
                <span
                  className={`relative mt-8 inline-flex h-11 w-11 items-center justify-center rounded-full border transition duration-300 group-hover:rotate-45 ${
                    d.featured ? "border-white/20 text-white group-hover:bg-brand group-hover:border-brand" : "border-slate-200 text-ink group-hover:border-brand group-hover:bg-brand group-hover:text-white"
                  } ${d.wide ? "md:mt-0" : ""}`}
                  aria-hidden="true"
                >
                  <HiArrowUpRight className="h-5 w-5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
